import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import config from '../config';
import './BillingBoard.css';

const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function BillingBoard() {
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [canManage, setCanManage] = useState(false);
  const [archiveProject, setArchiveProject] = useState(null);
  const [archiveNumber, setArchiveNumber] = useState('');
  const [archiveError, setArchiveError] = useState('');
  const [archiveBusy, setArchiveBusy] = useState(false);
  const archiveDialog = useRef(null);
  const draggedProject = useRef(null);
  const [draggingId, setDraggingId] = useState(null);
  const [archiveOver, setArchiveOver] = useState(false);
  const canArchive = project => canManage && project.column === 'facturation' && project.final_invoice_created && !archiveBusy && !archiveProject;
  const openArchive = project => {
    if (!canArchive(project)) return;
    setArchiveNumber(''); setArchiveError(''); setArchiveProject(project);
  };
  const endDrag = () => { draggedProject.current = null; setDraggingId(null); setArchiveOver(false); };
  const dropArchive = event => {
    const project = projects.find(row => String(row.id) === String(draggedProject.current));
    if (!project || !canArchive(project)) return;
    event.preventDefault();
    endDrag();
    openArchive(project);
  };
  useEffect(() => {
    if (archiveProject && !archiveDialog.current.open) archiveDialog.current.showModal();
    else if (!archiveProject && archiveDialog.current?.open) archiveDialog.current.close();
  }, [archiveProject]);
  const archive = async event => {
    event.preventDefault();
    if (archiveBusy || !archiveNumber.trim()) return;
    setArchiveBusy(true); setArchiveError('');
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem(config.tokenKey) || ''}`, 'Content-Type': 'application/json' };
      const response = await fetch('https://kanban.ehv.ams-crm.fr/api/billing.php', { method: 'POST', headers, body: JSON.stringify({ action: 'archive_project', project_id: archiveProject.id, archive_number: archiveNumber.trim() }) });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Archivage impossible.');
      setProjects(current => current.map(project => project.id === archiveProject.id ? { ...project, column: 'archive', archive_number: result.archive_number } : project));
      setArchiveProject(null); setRefresh(value => value + 1);
    } catch (e) { setArchiveError(e.message); }
    finally { setArchiveBusy(false); }
  };
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    let running = false;
    const load = async () => {
      if (running) return;
      running = true;
      try {
        const token = localStorage.getItem(config.tokenKey);
        if (!token) throw new Error('Reconnectez-vous au CRM.');
        const options = { headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }, cache: 'no-store', signal: controller.signal };
        const login = await fetch('https://kanban.ehv.ams-crm.fr/api/auto_login.php', options);
        const session = await login.json();
        if (!login.ok || !session.success) throw new Error('Session expirée. Reconnectez-vous au CRM.');
        const response = await fetch('https://kanban.ehv.ams-crm.fr/api/billing.php?action=billing_board', options);
        const payload = await response.json();
        if (!response.ok || !payload.success) throw new Error(payload.error || 'Accès à la facturation indisponible.');
        if (active) { setProjects(payload.projects || []); setCanManage(Boolean(payload.can_manage)); setError(''); }
      } catch (e) {
        if (active) { setProjects([]); setCanManage(false); setError(e.message); }
      } finally { running = false; if (active) setLoading(false); }
    };
    load();
    const interval = window.setInterval(load, 30000);
    window.addEventListener('focus', load);
    return () => { active = false; controller.abort(); window.clearInterval(interval); window.removeEventListener('focus', load); };
  }, [refresh]);
  if (new URLSearchParams(location.search).has('project_id')) return <Navigate replace to={`/suivi-facturation${location.search}`} />;
  const matches = projects.filter(project => normalize([project.ehv_number, project.client_name, project.city, project.title, project.archive_number].join(' ')).includes(normalize(query.trim())));
  return <main className="billing-board">
    <header><div><small>CRM EHV</small><h1>Facturation</h1><p>Après la facture finale, glissez la tuile dans Archive, puis renseignez le numéro d’archive pour confirmer.</p></div><button type="button" onClick={() => setRefresh(value => value + 1)}>Actualiser</button></header>
    <label className="billing-board-search">Rechercher une affaire<input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="N° EHV, client, commune, intitulé…" /></label>
    {error && <p role="alert" className="billing-board-error">{error}</p>}
    {loading && <p role="status">Chargement des affaires…</p>}
    <div className="billing-board-columns">{[['facturation', 'Facturation'], ['archive', 'Archive']].map(([key, label]) => {
      const rows = matches.filter(project => project.column === key);
      return <section className={`billing-board-column ${key}${key === 'archive' && archiveOver ? ' drop-ready' : ''}`} key={key} aria-label={label}
        onDragOver={event => { if (key !== 'archive' || !draggedProject.current || !canManage) return; event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setArchiveOver(true); }}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setArchiveOver(false); }}
        onDrop={key === 'archive' ? dropArchive : undefined}>
        <h2>{label}<span>{rows.length}</span></h2>
        {key === 'archive' && draggingId !== null && <p className="billing-drop-hint" role="status">Déposez ici pour renseigner le numéro d’archive</p>}
        <div className="billing-board-cards">{rows.map(project => <article className={`billing-board-card${canArchive(project) ? ' draggable' : ''}${draggingId === project.id ? ' dragging' : ''}`} key={project.id}
          draggable={Boolean(canArchive(project))}
          onDragStart={event => { if (!canArchive(project)) { event.preventDefault(); return; } draggedProject.current = project.id; setDraggingId(project.id); event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(project.id)); }}
          onDragEnd={endDrag}>
          {canArchive(project) && <div className="billing-drag-handle" aria-hidden="true">⠿ Glisser vers Archive</div>}
          <Link draggable={false} to={`/suivi-facturation?project_id=${encodeURIComponent(project.id)}`}>
          <small>EHV {project.ehv_number} · {project.client_name || 'Client non renseigné'}</small>
          <strong>{project.city}</strong><p>{project.title}</p><span>{key === 'archive' ? 'Consulter la facturation →' : 'Ouvrir la facturation →'}</span>
        </Link>{key === 'archive' ? <p className="billing-archive-number">N° d’archive : <strong>{project.archive_number}</strong></p> : <div className="billing-archive-action"><small>{project.final_invoice_created ? 'Facture finale créée · à archiver' : 'Facture finale requise avant archivage'}</small>{canManage && <button type="button" disabled={!canArchive(project)} onClick={() => openArchive(project)}>Archiver</button>}</div>}</article>)}{!rows.length && !loading && !error && <p className="billing-board-empty">{query ? 'Aucune affaire ne correspond à la recherche.' : key === 'archive' ? 'Aucune affaire archivée.' : 'Aucune affaire à facturer.'}</p>}</div>
      </section>;
    })}</div>
    <dialog ref={archiveDialog} className="billing-archive-dialog" aria-labelledby="archive-title" onCancel={event => { if (archiveBusy) event.preventDefault(); else setArchiveProject(null); }} onClose={() => setArchiveProject(null)}>
      {archiveProject && <form onSubmit={archive}>
        <h2 id="archive-title">Archiver l’affaire</h2><p>EHV {archiveProject.ehv_number} · {archiveProject.city}</p>
        <label>Numéro d’archive obligatoire<input autoFocus required maxLength={100} value={archiveNumber} disabled={archiveBusy} onChange={event => setArchiveNumber(event.target.value)} /></label>
        <p>Le dossier sera déplacé dans Archive uniquement après confirmation.</p>
        {archiveError && <p role="alert" className="billing-board-error">{archiveError}</p>}
        <footer><button type="button" disabled={archiveBusy} onClick={() => setArchiveProject(null)}>Annuler</button><button type="submit" disabled={archiveBusy || !archiveNumber.trim()}>{archiveBusy ? 'Archivage…' : 'Confirmer l’archivage'}</button></footer>
      </form>}
    </dialog>
  </main>;
}
